/** `/portal` — the investor portal (SDD §7.4 "Investor portal (external)", gap screen OD-4 — now the real
 *  token flow, mandate §2).
 *
 *  GRANT-GATED, on every binding: the page renders NOTHING private without a consumed grant token. A link
 *  carries `?grant=<token>`; the page exchanges it through `portal.session.login` (throttled server-side; a
 *  wrong, revoked and expired token all answer with the same refusal so the endpoint is not an oracle), and
 *  every view after that is served under the portal session with its own audit id — displayed, because "every
 *  view is audited" is a promise the investor should be able to read. Revocation ends the session at the next
 *  request; the refusal state says exactly that and nothing else.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { api } from '@/client/api';
import { SimulatedBadge } from '@pointfive/ui';

type PortalState =
  | { kind: 'landing' }
  | { kind: 'authenticating' }
  | { kind: 'refused'; message: string }
  | {
      kind: 'active';
      investorName: string;
      traderName: string;
      expiresAt: string;
      views: readonly ('score' | 'terms' | 'payments')[];
      simulated: boolean;
      score: {
        watermark: string;
        score: number;
        tier: string;
        pillars: readonly { label: string; value: number }[];
        auditId: string;
      } | null;
      payments: {
        items: readonly { id: string; amount: number; currency: string; status: string; at: string }[];
        auditId: string;
      } | null;
    };

const card: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--card-line)',
  boxShadow: 'var(--shadow-card)',
  borderRadius: '16px',
  padding: '16px 18px',
};
const label: React.CSSProperties = {
  fontSize: '9.5px',
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  color: 'var(--muted2)',
  fontWeight: 600,
};

export function PortalPage() {
  // From the ROUTER's location, so a client-side navigation to a different grant re-runs the exchange —
  // window.location read once would keep serving the first token this mount ever saw.
  const { search } = useLocation();
  const token = useMemo(() => new URLSearchParams(search).get('grant'), [search]);
  const [state, setState] = useState<PortalState>(token ? { kind: 'authenticating' } : { kind: 'landing' });

  const open = useCallback(async (grantToken: string) => {
    setState({ kind: 'authenticating' });
    try {
      const session = await api.portal.session.login({ grantToken });
      const wants = session.views;
      const [score, payments] = await Promise.all([
        wants.includes('score') ? api.portal.views.score() : Promise.resolve(null),
        wants.includes('payments') ? api.portal.views.payments() : Promise.resolve(null),
      ]);
      setState({
        kind: 'active',
        investorName: session.investorName,
        traderName: session.traderName,
        expiresAt: session.expiresAt,
        views: wants,
        simulated: session.meta.mode === 'simulated',
        score: score
          ? {
              watermark: score.watermark,
              score: score.score,
              tier: score.tier,
              pillars: score.pillars.map((p) => ({ label: p.label, value: p.value })),
              auditId: score.auditId,
            }
          : null,
        payments: payments
          ? { items: payments.items.map((p) => ({ ...p })), auditId: payments.auditId }
          : null,
      });
    } catch (error) {
      // One refusal for every failure, by design: wrong, revoked and expired links answer identically.
      setState({
        kind: 'refused',
        message: error instanceof Error ? error.message : 'This grant link is not active.',
      });
    }
  }, []);

  useEffect(() => {
    if (token) void open(token);
    else setState({ kind: 'landing' });
  }, [token, open]);

  const signOut = async () => {
    try {
      await api.portal.session.logout();
    } finally {
      setState({ kind: 'landing' });
      window.history.replaceState({}, '', '/portal');
    }
  };

  return (
    <div
      data-page="portal"
      data-theme="dark"
      style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontSize: '13px' }}
    >
      <div
        style={{
          maxWidth: '860px',
          margin: '0 auto',
          padding: '24px 12px 40px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '12px',
              background: 'var(--rail)',
              color: 'var(--rail-ink)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="ph-fill ph-asterisk" style={{ fontSize: '19px' }}></i>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13.5px', fontWeight: 700, letterSpacing: '-0.01em' }}>
              Investor portal
            </div>
            <div style={{ fontSize: '10.5px', color: 'var(--muted)' }}>
              Verified performance shared with you · read-only
            </div>
          </div>
          {state.kind === 'active' && state.simulated ? <SimulatedBadge what="portal data" /> : null}
          {state.kind === 'active' ? (
            <button
              onClick={() => void signOut()}
              aria-label="Sign out of the portal"
              style={{
                fontSize: '11px',
                padding: '6px 14px',
                borderRadius: '99px',
                border: '1px solid var(--l10)',
                background: 'transparent',
                color: 'var(--text-mid)',
                cursor: 'pointer',
              }}
            >
              Sign out
            </button>
          ) : null}
        </div>

        {state.kind === 'landing' ? (
          <div style={card}>
            <div style={label}>No grant presented</div>
            <p style={{ fontSize: '12px', color: 'var(--text-mid)', lineHeight: 1.7, marginTop: '8px' }}>
              This portal opens from an investor link. Ask the trader who shared access with you for your link
              — it carries a one-time credential and nothing here renders without it.
            </p>
          </div>
        ) : null}

        {state.kind === 'authenticating' ? (
          <div style={card} role="status" aria-live="polite">
            <div style={label}>Opening your grant…</div>
          </div>
        ) : null}

        {state.kind === 'refused' ? (
          <div style={card} role="alert">
            <div style={{ ...label, color: 'var(--neg)' }}>Access refused</div>
            <p style={{ fontSize: '12px', color: 'var(--text-mid)', lineHeight: 1.7, marginTop: '8px' }}>
              {state.message} A link stops answering when the trader revokes it, when it expires, or when it
              was never valid — the portal deliberately does not say which. If you believe you should have
              access, ask the trader for a fresh link.
            </p>
          </div>
        ) : null}

        {state.kind === 'active' ? (
          <>
            <div style={card}>
              <div style={label}>Grant</div>
              <div style={{ fontSize: '12.5px', marginTop: '6px', lineHeight: 1.7 }}>
                <b>{state.investorName}</b> viewing <b>{state.traderName}</b>’s shared performance. Session
                ends {state.expiresAt.slice(11, 16)} UTC. Scope: {state.views.join(', ')}.
              </div>
            </div>

            {state.score ? (
              <div style={card}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                  <div style={label}>PointFive Score</div>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: '9.5px', color: 'var(--faint)' }}>{state.score.watermark}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginTop: '8px' }}>
                  <span style={{ fontSize: '34px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {state.score.score}
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--muted)' }}>{state.score.tier}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px' }}>
                  {state.score.pillars.map((p) => (
                    <div key={p.label} style={{ display: 'flex', gap: '10px', fontSize: '11.5px' }}>
                      <span style={{ flex: 1, color: 'var(--text-mid)' }}>{p.label}</span>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{p.value}</span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: '9.5px', color: 'var(--faint)', marginTop: '10px' }}>
                  This view was audited · {state.score.auditId}
                </div>
              </div>
            ) : null}

            {state.payments ? (
              <div style={card}>
                <div style={label}>Payments</div>
                {state.payments.items.length === 0 ? (
                  <p style={{ fontSize: '11.5px', color: 'var(--muted)', marginTop: '8px' }}>
                    No payments recorded under this grant.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', marginTop: '8px' }}>
                    {state.payments.items.map((p, i) => (
                      <div
                        key={p.id}
                        style={{
                          display: 'flex',
                          gap: '10px',
                          padding: '8px 0',
                          borderTop: i ? '1px solid var(--l6)' : 'none',
                          fontSize: '12px',
                        }}
                      >
                        <span style={{ flex: 1 }}>{p.at.slice(0, 10)}</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {p.currency} {p.amount.toLocaleString('en-US')}
                        </span>
                        <span style={{ color: 'var(--muted)', textTransform: 'capitalize' }}>{p.status}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: '9.5px', color: 'var(--faint)', marginTop: '10px' }}>
                  This view was audited · {state.payments.auditId}
                </div>
              </div>
            ) : null}
          </>
        ) : null}

        <div style={{ fontSize: '9.5px', color: 'var(--faint)' }}>
          Every view is audited · grants are time-boxed and instantly revocable
        </div>
      </div>
    </div>
  );
}
